/**
 * Google Drive as a `RemoteStore` — the first adapter behind the port.
 *
 * Everything Drive-shaped stops here: `q` query strings, `appProperties`,
 * `modifiedTime`, the folder mime type, multipart vs resumable uploads. Above
 * the port the sync layer sees folders, named entries and an opaque `version`.
 *
 * Registered at start-up by `src/wiring.ts`; nothing in `src/cloud/sync`
 * imports this file.
 */
import type { PutInput, RemoteCapabilities, RemoteEntry, RemoteId, RemoteStore } from '@/cloud/store';
import { getSilentToken } from './auth';
import {
  DRIVE_LABEL,
  FOLDER_MIME,
  downloadBlob,
  driveFolderUrl,
  ensureFolder,
  escapeQuery,
  findByName,
  getFileMeta,
  isDriveError,
  listFilesAll,
  moveFile,
  patchAppProperties,
  uploadBlob,
  type DriveFile,
} from './client';
import { README_FILE_NAME, README_TEXT } from './readme-text';

/**
 * drive.file only shows files this app created or the user picked, so a listing
 * is always complete for our own tree. Ids survive a move, which is what lets
 * `attachments.driveFileId` stay valid after a receipt is filed.
 */
const DRIVE_CAPABILITIES: RemoteCapabilities = {
  metadata: true,
  versioning: true,
  contentHash: true,
  stableIdsAcrossMove: true,
  // Drive v3 has no If-Match on files.update, so `ifVersion` is ignored here
  // and the sync layer keeps its stat-then-write check. Single writer.
  conditionalPut: false,
};

/** Drive file metadata → the port's entry shape. */
export function toEntry(file: DriveFile): RemoteEntry {
  const size = file.size === undefined ? undefined : Number(file.size);
  return {
    id: file.id,
    name: file.name,
    mediaType: file.mimeType,
    isFolder: file.mimeType === FOLDER_MIME,
    ...(file.parents ? { parents: file.parents } : {}),
    ...(size !== undefined && Number.isFinite(size) ? { size } : {}),
    ...(file.createdTime ? { createdAt: file.createdTime } : {}),
    ...(file.modifiedTime ? { modifiedAt: file.modifiedTime, version: file.modifiedTime } : {}),
    ...(file.md5Checksum ? { contentHash: file.md5Checksum } : {}),
    ...(file.appProperties ? { metadata: file.appProperties } : {}),
    ...(file.trashed !== undefined ? { trashed: file.trashed } : {}),
  };
}

export const driveStore: RemoteStore = {
  id: 'drive',
  label: DRIVE_LABEL,
  capabilities: DRIVE_CAPABILITIES,
  readme: { name: README_FILE_NAME, text: README_TEXT },

  async isAuthorized(): Promise<boolean> {
    return !!(await getSilentToken());
  },

  async ensureFolder(name: string, parentId?: RemoteId): Promise<RemoteEntry> {
    return toEntry(await ensureFolder(name, parentId));
  },

  async list(parentId: RemoteId): Promise<RemoteEntry[]> {
    const files = await listFilesAll(`'${escapeQuery(parentId)}' in parents and trashed = false`);
    return files.map(toEntry);
  },

  async findByName(name: string, parentId?: RemoteId, kind?: 'file' | 'folder'): Promise<RemoteEntry | undefined> {
    if (kind !== 'file') {
      const found = await findByName(name, parentId, kind === 'folder' ? FOLDER_MIME : undefined);
      return found ? toEntry(found) : undefined;
    }
    // `findByName` filters by an exact mime type; "anything but a folder" needs the query.
    const clauses = [`name = '${escapeQuery(name)}'`, 'trashed = false', `mimeType != '${FOLDER_MIME}'`];
    if (parentId) clauses.push(`'${escapeQuery(parentId)}' in parents`);
    const files = await listFilesAll(clauses.join(' and '));
    files.sort((a, b) => (a.createdTime ?? '').localeCompare(b.createdTime ?? ''));
    const first = files[0];
    return first ? toEntry(first) : undefined;
  },

  async stat(id: RemoteId): Promise<RemoteEntry | undefined> {
    try {
      return toEntry(await getFileMeta(id));
    } catch (e) {
      if (isDriveError(e, 'not_found')) return undefined;
      throw e;
    }
  },

  async put(input: PutInput): Promise<RemoteEntry> {
    return toEntry(
      await uploadBlob({
        name: input.name,
        parentId: input.parentId,
        blob: input.blob,
        ...(input.mediaType ? { mediaType: input.mediaType } : {}),
        ...(input.metadata ? { appProperties: input.metadata } : {}),
        ...(input.id ? { fileId: input.id } : {}),
      }),
    );
  },

  get(id: RemoteId): Promise<Blob> {
    return downloadBlob(id);
  },

  async move(id: RemoteId, from: RemoteId, to: RemoteId, newName?: string): Promise<RemoteEntry> {
    return toEntry(await moveFile(id, from, to, newName));
  },

  async setMetadata(id: RemoteId, metadata: Record<string, string | null>): Promise<void> {
    await patchAppProperties(id, metadata);
  },

  folderUrl(id: RemoteId): string | null {
    return driveFolderUrl(id);
  },

  /** Drive's viewer for a single file the app created (drive.file scope). */
  fileUrl(id: RemoteId): string | null {
    return `https://drive.google.com/file/d/${encodeURIComponent(id)}/view`;
  },
};
